<template>
  <bottom-sheet
    ref="sheet"
    :class="['chatbot-bottom-sheet', { 'chatbot-history-mode': showHistory, 'chatbot-overlay-open': showActionSheet || showDeleteConfirm }]"
    block-content
    variant="beige"
    @close="$emit('close')"
  >
    <template v-slot:header>
      <div class="chatbot-header">
        <!-- Logo+title and the new-chat icon occupy the same slot. When a conversation
             starts the logo+title fade out while drifting up, then (after a short delay,
             via the staggered enter transition) the new-chat icon fades in drifting up
             into place. -->
        <div class="header-brand">
          <transition name="brand-swap">
            <div v-if="conversationStarted" key="new-chat" class="row">
              <img
                class="icon icon-new-chat"
                :src="require('@/assets/chat-bot/new-chat.svg')"
                @click.stop="startNewConversation"
                @mousedown.stop
                @touchstart.stop
              />
            </div>
            <div v-else key="logo" class="row">
              <img class="icon" :src="require('@/assets/chat-bot/pink-double-star.svg')" aria-hidden="true" />
              <span class="title">רייזאפ צ׳אט</span>
            </div>
          </transition>
        </div>
        <div class="header-actions">
          <span class="header-action-icon-wrapper">
            <img
              class="header-action-icon"
              :src="require('@/assets/chat-bot/chat-history-list.svg')"
              @click.stop="toggleHistoryView"
              @mousedown.stop
              @touchstart.stop
            />
            <span v-if="hasUnreadInOtherConversation" class="header-action-icon-dot" aria-hidden="true" />
          </span>
          <img
            class="header-action-icon close"
            :src="require('@/assets/chat-bot/close.svg')"
            @click.stop="closeChat"
            @mousedown.stop
            @touchstart.stop
          />
        </div>
      </div>
    </template>

    <!-- Beige backdrop sits behind the fading views so the white sheet never shows
         through during the opacity transition. -->
    <div class="chat-body">
    <transition name="chat-view">
    <div
      v-if="historyLoading || conversationLoading"
      key="loading"
      class="pre-loading"
    >
      <lottie-player src="/animations/chat-pre-loading.json" autoplay loop />
      <span class="pre-loading-text">רק עוד רגע</span>
    </div>

    <conversation-history
      v-else-if="showHistory"
      key="history"
      :conversations="conversations"
      @select="loadConversation"
      @deleted="onConversationDeleted"
      @open-menu="openConversationMenu"
    />

    <div
      v-else
      :key="`chat-${chatSessionNonce}`"
      class="chat-shell"
    >
      <div
        ref="scroller"
        class="messages"
        :class="{ 'messages--entering': entering }"
        :style="entering ? entranceStyle : null"
      >
        <div v-if="showCxUpdateCard" class="cx-update-card">
          <div class="cx-update-card__illustration">
            <img class="cx-update-card__flower" :src="require('@/assets/chat-bot/cx-update-flower.svg')" aria-hidden="true" />
            <div class="cx-update-card__new-label">חדש!</div>
            <img class="cx-update-card__person" :src="require('@/assets/chat-bot/cx-update-person.png')" aria-hidden="true" />
          </div>
          <div class="cx-update-card__content">
            <div class="cx-update-card__title">צריכים עזרה טכנית? הצ׳אט על זה!</div>
            <div class="cx-update-card__body">הצ׳אט השתדרג, ומהיום הוא יודע לענות גם על שאלות של תמיכה טכנית.</div>
            <div class="cx-update-card__cta" @click="dismissCxUpdateCard">הבנתי, תודה</div>
          </div>
        </div>
        <template v-if="messages.length === 0">
          <div class="intro-suggestions">
            <button
              v-for="q in introQuestions"
              :key="q"
              type="button"
              class="intro-button"
              @click="onIntroClick(q)"
            >
              <span>{{ q }}</span>
              <img :src="require('@/assets/chat-bot/arrow_back.svg')" class="intro-arrow" />
            </button>
          </div>
        </template>

        <div
          v-for="(message, index) in messages"
          :key="message._id"
          class="bubble-row"
          :class="bubbleRowClass(message)"
          :style="entering ? { '--enter-index': entranceIndex(index) } : null"
        >
          <div
            class="bubble"
            :class="[bubbleClass(message), { 'is-revealing': message._id === revealingMessageId }]"
            :style="message._id === revealingMessageId ? revealStyle : null"
          >
            <div v-if="message.senderId === cxRole" class="cx-author-row">
              <img class="cx-author-badge" :src="require('@/assets/chat-bot/team-badge.svg')" alt="" aria-hidden="true" />
              <span class="cx-author">{{ cxAuthorLabel(message) }}</span>
            </div>
            <message-blocks
              :blocks="message.payload.blocks || []"
              :revealing="message._id === revealingMessageId"
              @media-loaded="onMediaLoaded"
              @escalation-accept="onEscalationAccept"
              @escalation-dismiss="onEscalationDismiss"
              @escalation-open="onEscalationOpen"
              @escalation-open-elsewhere="onEscalationOpenElsewhere"
              @csat-score="onCsatScore"
              @csat-comment="onCsatComment"
              @csat-comment-focus="onCsatCommentFocus"
              @csat-comment-blur="onCsatCommentBlur"
              @image-open="onImageOpen"
              @csat-submit="onCsatSubmit"
            />
            <div v-if="showTimestamp(message)" class="bubble-time">{{ formatTimestamp(message.createdAt) }}</div>
          </div>
        </div>

        <!-- Hidden while the team owns the thread: nobody is typing on our side. -->
        <div v-if="pending && !isEscalated" class="bubble bubble-assistant loading">
          <span class="loading-indicator">
            <lottie-player
              src="/animations/chat-answer-loader.json"
              autoplay
              loop
              style="width: 26px; height: 26px; transform: translateY(-1px);"
            />
            <span class="text-shimmer">אני על זה...</span>
          </span>
        </div>
        <div v-if="csatCommentFocused" class="composer-spacer" :style="{ height: `${composerSpacerHeight}px` }" aria-hidden="true" />
      </div>

      <form v-show="!csatCommentFocused" ref="composer" class="composer" @submit.prevent="onSubmit">
        <div class="composer-input-area">
          <div v-if="attachments.length" class="composer-input-area__bg" />
          <div v-if="attachments.length" class="attachment-chip-list">
            <div v-for="item in attachments" :key="item.id" class="attachment-chip">
              <img
                v-if="item.contentType.startsWith('image/')"
                class="attachment-chip__thumb"
                :src="item.previewUrl"
                alt=""
              />
              <video
                v-else-if="item.contentType.startsWith('video/')"
                class="attachment-chip__thumb"
                :src="item.previewUrl"
                muted
                playsinline
                preload="metadata"
              />
              <span class="attachment-chip__name">{{ item.name }}</span>
              <span class="attachment-chip__divider" />
              <span v-if="item.data === null" class="attachment-chip__spinner" aria-label="טוען קובץ..." />
              <button type="button" class="attachment-chip__remove" @click="removeAttachment(item.id)" aria-label="הסר קובץ">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M12 4L4 12"
                    stroke="#202125"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4 4L12 12"
                    stroke="#202125"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="composer-pill">
            <input
              ref="fileInput"
              type="file"
              class="composer-file-input"
              multiple
              :accept="attachmentAccept"
              @change="onFileSelected"
            />
            <button
              type="submit"
              class="composer-send"
              :disabled="!canSend || pending"
              aria-label="שלח"
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <circle cx="20" cy="20" r="20" fill="#5D7AFD" />
                <path d="M15.825 21L21.425 26.6L20 28L12 20L20 12L21.425 13.4L15.825 19H28V21H15.825Z" fill="white" />
              </svg>
            </button>
            <textarea
              ref="input"
              v-model="draft"
              class="composer-input"
              :placeholder="composerPlaceholder"
              autocomplete="off"
              rows="1"
              :disabled="pending"
              @input="autoResize"
              @focus="onComposerFocus"
              @keydown.enter.exact="onEnter"
              @keydown.enter.meta.exact.prevent="insertNewline"
              @keydown.enter.ctrl.exact.prevent="insertNewline"
            />
            <button
              v-if="isEscalated || allowUploadingImageToChatbot"
              type="button"
              class="composer-attach"
              aria-label="צרף קובץ"
              :disabled="attachments.length >= maxAttachments"
              @click="$refs.fileInput.click()"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 12H19"
                  stroke="#4868FF"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 5V19"
                  stroke="#4868FF"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <transition
          name="disclaimer"
          @enter="expandDisclaimer"
          @after-enter="settleDisclaimer"
          @leave="collapseDisclaimer"
        >
          <div v-if="!isEscalated" class="composer-disclaimer">הצ׳אט עלול לטעות לפעמים. שווה לבדוק שוב מידע חשוב</div>
        </transition>
      </form>
    </div>
    </transition>
    </div>

    <!-- Action sheet for a long-pressed conversation in history -->
    <div
      v-if="showActionSheet"
      class="fullscreen-overlay"
      @click.self="closeConversationMenu"
    >
      <div class="action-sheet">
        <div class="action-sheet-handle" />
        <div class="action-sheet-menu">
          <div class="action-sheet-item" @click="handleMenuContinue">
            <img :src="require('@/assets/chat-bot/continue-chat.svg')" class="action-icon" />
            <span>להמשיך את השיחה</span>
          </div>
          <div class="action-sheet-item" @click="handleMenuDelete">
            <img :src="require('@/assets/chat-bot/trash.svg')" class="action-icon" />
            <span>למחוק את השיחה</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation -->
    <div
      v-if="showDeleteConfirm"
      class="fullscreen-overlay fullscreen-overlay--center"
      @click.self="closeDeleteConfirm"
    >
      <div class="confirm-card">
        <popup
          title="האם למחוק את השיחה מרשימת השיחות שלך?"
          :buttons="deleteButtons"
          :close-action="closeDeleteConfirm"
        >
          <template v-slot:content>
            <div class="delete-subtitle">
              השיחה לא תהיה זמינה יותר אחרי המחיקה.
            </div>
          </template>
        </popup>
      </div>
    </div>

    <!-- Image lightbox. Moved to document.body on mount (see mounted()): the
         bottom sheet animates itself with a CSS transform, which makes it the
         containing block for any position:fixed descendant — without this,
         "full screen" would only mean full screen of the chat panel. -->
    <div
      v-show="lightboxImageUrl"
      ref="lightbox"
      class="fullscreen-overlay fullscreen-overlay--center"
      @click.self="closeLightbox"
    >
      <img
        class="lightbox-close"
        :src="require('@/assets/chat-bot/close.svg')"
        @click="closeLightbox"
      />
      <img v-if="lightboxImageUrl" :src="lightboxImageUrl" class="lightbox-image" @click="closeLightbox" />
    </div>
  </bottom-sheet>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import CashflowAssistantApi from '@/api/CashflowAssistant';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import Intercom from '@/Intercom';
import { hasCoarsePointer } from '@/utils/deviceUtils';
import {
  CHAT_CONVERSATION_RESUME_KEY,
  CHAT_CONVERSATION_RESUME_WINDOW_MS,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_TOO_LARGE_MESSAGE,
  ATTACHMENT_TYPE_MESSAGE,
  ATTACHMENT_TYPE_MESSAGE_NO_VIDEO,
  ATTACHMENT_TYPE_MESSAGE_IMAGE_ONLY,
  ATTACHMENT_COUNT_MESSAGE,
  MAX_ATTACHMENTS,
  ATTACHMENT_TOTAL_MAX_BYTES,
  IMAGE_MIME_BY_MAGIC,
  ATTACHMENT_TOTAL_TOO_LARGE_MESSAGE,
  CX_ESCALATED_PLACEHOLDER,
  CSAT_TITLE_GENERIC,
  CSAT_TITLE_PREFIX,
  CSAT_MAX_AGE_MS,
  CSAT_RATED_STORAGE_KEY,
  CSAT_RATED_MAX_ENTRIES,
  CX_ESCALATION_CLOSED_MESSAGE,
  CX_ESCALATION_STARTED_MESSAGE,
  CX_SEND_FAILED_MESSAGE,
  CX_TEAM_LABEL,
  DEFAULT_ERROR_MESSAGE,
  INTRO_QUESTIONS,
  MESSAGE_ROLES,
  REQUEST_CONFIG,
  REVEAL_CONFIG,
} from '@/constants/chat';

import ConversationHistory from './ConversationHistory.vue';
import MessageBlocks from './MessageBlocks.vue';
import { clearCandidate, clearCandidateIn, escalatedConversationIds, recordCandidate } from './aiChatCsat';
import { readIdList, rememberId } from './storedIdList';
import { isValidResponse, isValidBlock, isMediaBlock } from './chat-validation';
import { formatMessageTime, revealDurationMs } from './chat-formatting';

const CX_UPDATE_CARD_DISMISSED_KEY = 'chatbot_cx_update_card_dismissed';
const FORBIDDEN_CHARS_PATTERN = /^[^<>{}[\]\\/]*$/;
const FORBIDDEN_CHARS_MESSAGE = 'לא ניתן לשלוח הודעה עם התווים הבאים: / \\ [ ] { } < >';
const RATE_LIMIT_MESSAGE = 'הגעת למגבלת ההודעות להיום. אפשר להמשיך את השיחה מחר';
const ESCALATION_DISPLAY_DURATION_MS = 1000;
// Only show the in-chat loader if loading runs longer than this. Fast loads finish
// before the timer fires, so the loader never flashes for a split second.
const LOADER_SHOW_DELAY_MS = 400;
// Opening a thread lifts the last few messages in one after another; anything
// older sits above the fold, so it lands at once.
const ENTRANCE_STAGGERED_MESSAGES = 8;
const ENTRANCE_STEP_MS = 80;
const ENTRANCE_DURATION_MS = 450;
const COMPOSER_LIFT_MS = 300;
const CSAT_REVEAL_MARGIN_PX = 12;
const CSAT_REVEAL_SETTLE_MS = 120;
// Above the tallest media block (320px video), well below a deliberate scroll-up.
const MEDIA_REPIN_THRESHOLD_PX = 400;

function prefersReducedMotion() {
  return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

export default {
  name: 'Chatbot',
  components: {
    BottomSheet: BaseUI.BottomSheet,
    Popup: BaseUI.Popup,
    ConversationHistory,
    MessageBlocks,
  },
  props: {
    // When provided, the chatbot opens and auto-sends this message on mount,
    // skipping the empty composer (e.g. the "יש לי בעיה" navigation-menu tile).
    initialMessage: {
      type: String,
      required: false,
      default: null,
    },
    initiator: {
      type: String,
      required: false,
      default: null,
    },
    suggestedQuestions: {
      type: Array,
      required: false,
      default: null,
    },
    // Opens straight into this thread, e.g. from the CX reply toast. Named
    // apart from the `conversationId` data property, which tracks the thread
    // actually on screen and changes as the customer navigates.
    initialConversationId: {
      type: String,
      required: false,
      default: null,
    },
    startWithTeam: {
      type: Boolean,
      required: false,
      default: false,
    },
    startWithTeamFallback: {
      type: Function,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      messages: [],
      draft: '',
      nextId: 1,
      conversationId: null,
      chatSessionNonce: 0,
      pending: false,
      // Server-owned, refreshed on every load, never inferred locally.
      escalation: null,
      // Set when a push arrives mid-send or mid-typing, drained once idle.
      refreshPending: false,
      closePending: false,
      // Asked once per conversation, so a second close event cannot re-ask.
      csatAskedFor: null,
      csatSubmittedFor: null,
      csatCommentFocused: false,
      composerSpacerHeight: 0,
      // Set once the team has had this conversation (open or closed escalation);
      // the team CSAT covers it, the AI CSAT never asks about it.
      handedToTeam: false,
      // Server record of already-rated conversations, fetched once on mount.
      ratedIntercomConversationIds: [],
      // Each item: { id, name, contentType, data, previewUrl }. data is null
      // while its base64 encode is still in flight; cleared after send.
      attachments: [],
      maxAttachments: MAX_ATTACHMENTS,
      conversations: [],
      showHistory: false,
      showActionSheet: false,
      showDeleteConfirm: false,
      lightboxImageUrl: null,
      deleteLoading: false,
      menuConversation: null,
      cxUpdateCardDismissed: localStorage.getItem(CX_UPDATE_CARD_DISMISSED_KEY) === 'true',
      lastClickedIntroQuestion: null,
      historyLoading: false,
      conversationLoading: false,
      loaderDelayTimer: null,
      revealingMessageId: null,
      entering: false,
      entranceCount: 0,
      entranceTimer: null,
      destroyed: false,
    };
  },
  computed: {
    ...mapGetters('featureFlags', {
      cxAgentUiChanges: 'cxAgentUiChanges',
      inlineCxEnabled: 'riseupChatCxInline',
      aiChatCsatEnabled: 'aiChatCsat',
      allowUploadingImageToChatbot: 'allowUploadingImageToChatbot',
    }),
    ...mapGetters('riseupChat', ['hasUnreadInOtherConversation']),
    isEscalated() {
      return _.get(this.escalation, 'status') === 'open';
    },
    cxRole() {
      return MESSAGE_ROLES.CX;
    },
    revealStyle() {
      return { '--reveal-duration': `${REVEAL_CONFIG.durationMs}ms` };
    },
    entranceStyle() {
      return { '--enter-step': `${ENTRANCE_STEP_MS}ms`, '--enter-duration': `${ENTRANCE_DURATION_MS}ms` };
    },
    composerPlaceholder() {
      return this.isEscalated ? CX_ESCALATED_PLACEHOLDER : 'כתוב הודעה';
    },
    attachmentAccept() {
      if (!this.isEscalated) {
        return ATTACHMENT_ACCEPT.split(',').filter(type => type.startsWith('image/')).join(',');
      }
      if (this.inlineCxEnabled) {
        return ATTACHMENT_ACCEPT;
      }
      return ATTACHMENT_ACCEPT.split(',').filter(type => !type.startsWith('video/')).join(',');
    },
    attachmentTypeMessage() {
      if (!this.isEscalated) {
        return ATTACHMENT_TYPE_MESSAGE_IMAGE_ONLY;
      }
      return this.inlineCxEnabled ? ATTACHMENT_TYPE_MESSAGE : ATTACHMENT_TYPE_MESSAGE_NO_VIDEO;
    },
    csatTitle() {
      const last = _.findLast(this.messages, m => m.senderId === MESSAGE_ROLES.CX && m.authorName);
      const firstName = last ? String(last.authorName).trim().split(' ')[0] : '';
      return firstName ? `${CSAT_TITLE_PREFIX} ${firstName}?` : CSAT_TITLE_GENERIC;
    },
    // A file on its own is a valid message; text alone still is too.
    canSend() {
      if (this.attachments.some(a => a.data === null)) { return false; }
      return Boolean(this.draft.trim()) || Boolean(this.attachments.length);
    },
    introQuestions() {
      return this.suggestedQuestions || INTRO_QUESTIONS;
    },
    showCxUpdateCard() {
      return this.cxAgentUiChanges && !this.cxUpdateCardDismissed;
    },
    // Only offer "new conversation" once the current one has started — a fresh, empty
    // chat already is a new conversation, so the button would be a no-op there.
    conversationStarted() {
      return this.messages.length > 0;
    },
    deleteButtons() {
      return [{
        title: 'כן, למחוק את השיחה',
        color: 'red',
        loading: this.deleteLoading,
        action: this.handleDeleteConversation,
      }];
    },
  },
  watch: {
    // A pointer only, so the content is fetched over the authenticated
    // connection rather than trusted from here.
    '$store.state.websocket.chatEvent': function onChatEvent(event) {
      this.handleChatEvent(event);
    },
    // Tracked on the value, not inside switchConversation: a conversation created
    // by sending the first message assigns conversationId directly.
    conversationId(newConversationId) {
      this.$store.commit('riseupChat/setOpenConversationId', newConversationId);
    },
  },
  mounted() {
    this.$store.commit('riseupChat/setChatOpen', true);
    this.ratedConversationsLoaded = this.fetchRatedConversations();

    // Before the early return below, so it is active when the chat opens
    // straight into sending an initial message.
    document.addEventListener('visibilitychange', this.refreshOnVisible);

    // Same reason: Vue 2 has no built-in teleport, so moving the node directly
    // is the equivalent for the one place this component needs to escape its
    // parent — must run regardless of which branch below returns early.
    if (this.$refs.lightbox) {
      document.body.appendChild(this.$refs.lightbox);
    }

    if (this.startWithTeam) {
      this.startTeamConversation();
      return;
    }

    // An explicit initial message (e.g. from the "יש לי בעיה" tile) takes
    // precedence over restoring the last conversation — start fresh and send it.
    if (this.initialMessage) {
      this.$nextTick(() => {
        this.sendUserText(this.initialMessage);
      });
      return;
    }

    this.restoreInitialConversation();
  },
  // A card that re-renders or leaves with its field focused never fires blur,
  // which would leave the composer hidden.
  updated() {
    if (this.csatCommentFocused && document.activeElement !== this.csatCommentField) {
      this.onCsatCommentBlur();
    }
  },
  beforeDestroy() {
    this.destroyed = true;
    this.clearLoaderDelay();
    clearTimeout(this.entranceTimer);
    this.stopCsatReveal();
    this.persistRecentConversation();
    document.removeEventListener('visibilitychange', this.refreshOnVisible);
    this.$store.commit('riseupChat/setOpenConversationId', null);
    this.$store.commit('riseupChat/setChatOpen', false);
    if (this.$refs.lightbox && this.$refs.lightbox.parentNode) {
      this.$refs.lightbox.parentNode.removeChild(this.$refs.lightbox);
    }
  },
  methods: {
    dismissCxUpdateCard() {
      this.cxUpdateCardDismissed = true;
      localStorage.setItem(CX_UPDATE_CARD_DISMISSED_KEY, 'true');
      Segment.trackUserInteraction('AIChatbot_CxUpdateCardDismissed');
    },
    // ─── Composer ──────────────────────────────────────────────────────
    // The keyboard lifts the composer over the thread; keep the latest message
    // in view. Desktop has no keyboard lift, and scrolling there would yank a
    // customer who scrolled up to re-read something before replying.
    onComposerFocus() {
      if (!hasCoarsePointer()) { return; }
      setTimeout(() => this.scrollToBottom({ smooth: true }), COMPOSER_LIFT_MS);
    },
    // The disclaimer's height is what moves the composer, so the height is
    // animated from its measured value rather than a guessed max-height.
    expandDisclaimer(el) {
      const { style } = el;
      const height = `${el.scrollHeight}px`;
      style.height = '0px';
      style.paddingTop = '0px';
      el.getBoundingClientRect();
      style.height = height;
      style.paddingTop = '';
    },
    settleDisclaimer(el) {
      const { style } = el;
      style.height = '';
    },
    collapseDisclaimer(el) {
      const { style } = el;
      style.height = `${el.scrollHeight}px`;
      el.getBoundingClientRect();
      style.height = '0px';
      style.paddingTop = '0px';
    },
    // An Enter that commits an IME composition is not a send. Touch keyboards
    // keep their newline default — they have no Ctrl/Cmd for a manual line
    // break (and some Android IMEs report every key as 229), so on them
    // sending stays on the send button.
    onEnter(event) {
      if (hasCoarsePointer()) { return; }
      if (event.isComposing || event.keyCode === 229) { return; }
      event.preventDefault();
      this.onSubmit();
    },
    // Browsers ignore a modified Enter in a textarea, so the line break is inserted by hand.
    insertNewline(event) {
      const input = event.target;
      input.setRangeText('\n', input.selectionStart, input.selectionEnd, 'end');
      this.draft = input.value;
      this.$nextTick(this.autoResize);
    },
    async onSubmit() {
      if (!this.canSend || this.pending) { return; }
      const text = this.draft.trim();
      this.draft = '';
      this.$nextTick(this.autoResize);
      await this.sendUserText(text);
    },
    onFileSelected(event) {
      const input = event.target;
      const files = Array.from(input.files || []);
      // Reset immediately so choosing the same file(s) twice still fires change.
      input.value = '';
      if (!files.length) { return; }
      const room = this.maxAttachments - this.attachments.length;
      if (room <= 0) {
        this.appendNotice(ATTACHMENT_COUNT_MESSAGE);
        return;
      }
      if (files.length > room) {
        this.appendNotice(ATTACHMENT_COUNT_MESSAGE);
      }
      files.slice(0, room).forEach(file => this.validateAndReadFile(file));
    },
    validateAndReadFile(file) {
      if (file.size > ATTACHMENT_MAX_BYTES) {
        this.appendNotice(ATTACHMENT_TOO_LARGE_MESSAGE);
        return;
      }
      const currentTotal = this.attachments.reduce((sum, a) => sum + a.size, 0);
      if (currentTotal + file.size > ATTACHMENT_TOTAL_MAX_BYTES) {
        this.appendNotice(ATTACHMENT_TOTAL_TOO_LARGE_MESSAGE);
        return;
      }
      if (!this.attachmentAccept.split(',').includes(file.type)) {
        this.appendNotice(this.attachmentTypeMessage);
        return;
      }
      this.readFile(file);
    },
    readFile(file) {
      const attachment = {
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        contentType: file.type,
        data: null,
        previewUrl: URL.createObjectURL(file),
      };
      this.attachments.push(attachment);
      const reader = new FileReader();
      reader.onload = () => {
        // Stale if the attachment was removed/cleared while this was reading.
        if (!this.attachments.includes(attachment)) { return; }
        // Strip the data: prefix; the API takes raw base64.
        attachment.data = String(reader.result || '').split(',')[1] || '';
      };
      reader.onerror = () => {
        if (!this.attachments.includes(attachment)) { return; }
        this.removeAttachment(attachment.id);
        this.appendNotice(DEFAULT_ERROR_MESSAGE);
      };
      reader.readAsDataURL(file);
    },
    removeAttachment(id) {
      const index = this.attachments.findIndex(a => a.id === id);
      if (index === -1) { return; }
      const [removed] = this.attachments.splice(index, 1);
      // Only reachable before send - a sent attachment's previewUrl stays
      // alive, it is what the bubble displays.
      if (removed.previewUrl) { URL.revokeObjectURL(removed.previewUrl); }
    },
    clearAttachments() {
      [...this.attachments].forEach(a => this.removeAttachment(a.id));
    },
    appendNotice(value) {
      this.appendMessage({
        senderId: MESSAGE_ROLES.AI,
        blocks: [{ type: 'notice', variant: 'validation', value }],
      });
    },
    autoResize() {
      const el = this.$refs.input;
      if (!el) { return; }
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },
    onIntroClick(question) {
      Segment.trackUserInteraction('AIChatbot_IntroQuestionClicked', { question });
      this.lastClickedIntroQuestion = question;
      this.sendUserText(question);
    },
    async sendUserText(text) {
      if (text && !FORBIDDEN_CHARS_PATTERN.test(text)) {
        Segment.trackUserInteraction('ChatbotForbiddenCharactersError');
        this.appendMessage({
          senderId: MESSAGE_ROLES.USER,
          blocks: [{ type: 'text', value: text }],
        });
        this.appendMessage({
          senderId: MESSAGE_ROLES.AI,
          blocks: [{ type: 'notice', variant: 'validation', value: FORBIDDEN_CHARS_MESSAGE }],
        });
        return;
      }

      if (this.lastClickedIntroQuestion !== text) {
        Segment.trackUserInteraction('AIChatbot_PromptSent');
      }
      this.lastClickedIntroQuestion = null;

      const { attachments } = this;
      this.attachments = [];
      const messageId = String(this.nextId);
      this.appendMessage({
        senderId: MESSAGE_ROLES.USER,
        blocks: [...(text ? [{ type: 'text', value: text }] : []), ...attachments.flatMap(a => this._attachmentBlocks(a))],
        createdAt: new Date().toISOString(),
      });
      this.$nextTick(this.blurKeyboard);
      this.pending = true;
      const nonceAtSend = this.chatSessionNonce;
      try {
        const requestBody = { text: !text && attachments.length ? ' ' : text };
        if (this.conversationId) { requestBody.conversationId = this.conversationId; }
        if (this.initiator) { requestBody.externalContext = { initiator: this.initiator }; }
        // Idempotency key for the relay to CX.
        requestBody.clientMessageId = `${Date.now()}${this.nextId}`;
        // Lets the server refuse to answer as the assistant if it cannot reach
        // carmella to confirm who owns the thread.
        const wasEscalated = this.isEscalated;
        if (wasEscalated) { requestBody.escalated = true; }
        if (attachments.length) {
          requestBody.attachments = attachments.map(a => ({ name: a.name, contentType: a.contentType, data: a.data }));
        }
        const response = await fetch(REQUEST_CONFIG.url, {
          method: REQUEST_CONFIG.method,
          headers: REQUEST_CONFIG.headers,
          body: JSON.stringify(requestBody),
        });
        if (response.status === 429) { throw new Error('rate_limited'); }
        if (!response.ok) { throw new Error('request_failed'); }
        const json = await response.json();
        if (!isValidResponse(json)) { throw new Error('invalid_response'); }
        if (nonceAtSend !== this.chatSessionNonce) { return; }
        if (json.conversationId) { this.conversationId = json.conversationId; }
        this.pending = false;
        // Relayed to the team; the reply arrives later as a cx message. Blocks are
        // usually empty, but the hand-over turn sends a confirmation.
        if (json.escalated === true) {
          // The server escalated on the assistant's own judgment, so the consent
          // path (openEscalation) never runs. Fire the same event it fires on
          // the hand-over turn only: every later relayed message also comes back
          // escalated, and the escalation metrics downstream count this event.
          if (!wasEscalated) {
            Segment.trackUserInteraction('cashflow_assistant_escalated_to_intercom', {
              conversationId: json.conversationId || this.conversationId,
              intercomConversationId: json.intercomConversationId,
              inline: this.inlineCxEnabled,
              auto: true,
            });
          }
          this.escalation = { status: 'open', intercomConversationId: json.intercomConversationId };
          this.handedToTeam = true;
          clearCandidate(this.conversationId);
          if (json.blocks && json.blocks.length) {
            await this.appendAnimatedMessage(MESSAGE_ROLES.AI, json.blocks, nonceAtSend);
          }
          return;
        }
        if (json.openConversationElsewhere) {
          this.appendMessage({
            senderId: MESSAGE_ROLES.AI,
            blocks: [{ type: 'escalation-open-elsewhere', conversationId: json.openConversationElsewhere }],
          });
          return;
        }
        // The team closed it between the last refresh and this send. The CSAT
        // key comes from the escalation this send was made under, so ask
        // before clearing it.
        if (json.escalationClosed === true) {
          this.appendMessage({
            senderId: MESSAGE_ROLES.AI,
            blocks: [{ type: 'notice', variant: 'system', noticeType: 'closed', value: CX_ESCALATION_CLOSED_MESSAGE }],
          });
          this.appendCsatCard();
          this.escalation = null;
          this.handedToTeam = true;
          // Same hand-over, learned from the close: the team CSAT owns this one.
          clearCandidate(this.conversationId);
          return;
        }
        // Before the animation: a close mid-typing must still leave the candidate.
        this.recordAiCsatCandidate({ escalated: json.shouldEscalate === true });
        await this.appendAnimatedMessage(MESSAGE_ROLES.AI, json.blocks, nonceAtSend);
        if (nonceAtSend !== this.chatSessionNonce) { return; }
        // Render the assistant's answer first, then ask whether to escalate to a human.
        if (json.shouldEscalate === true) {
          const consentBlock = json.escalationAlreadyOpen === true
            ? { type: 'escalation-transferred', status: 'pending' }
            : { type: 'escalation-consent', status: 'pending' };
          this.appendMessage({ senderId: MESSAGE_ROLES.AI, blocks: [consentBlock] });
          Segment.trackUserInteraction('CashflowAssistantEscalationPromptShown', {
            conversationId: this.conversationId,
            escalationAlreadyOpen: json.escalationAlreadyOpen === true,
          });
        }
      } catch (err) {
        if (nonceAtSend !== this.chatSessionNonce) { return; }
        const isRateLimit = err && err.message === 'rate_limited';
        // While the team owns the thread, a failure means undelivered.
        let message = isRateLimit ? RATE_LIMIT_MESSAGE : DEFAULT_ERROR_MESSAGE;
        if (this.isEscalated) { message = CX_SEND_FAILED_MESSAGE; }
        const variant = isRateLimit ? 'rate-limit' : 'error';
        this.pending = false;
        this.appendMessage({
          senderId: MESSAGE_ROLES.AI,
          blocks: [{ type: 'notice', variant, value: message }],
        });
      } finally {
        this.pending = false;
        if (attachments.length) { this._clearAttachmentSending(messageId); }
        this.drainPendingRefresh();
      }
    },
    // A push that arrived while this was busy still has to land.
    drainPendingRefresh() {
      if (this.destroyed || !this.refreshPending || !this.canRefreshNow()) { return; }
      this.refreshPending = false;
      const refreshed = this.reloadCurrentConversation();
      if (this.closePending) {
        this.closePending = false;
        refreshed.then(() => this.appendCsatCard());
      }
    },
    _storedImageBlocks(images) {
      if (!this.allowUploadingImageToChatbot || !Array.isArray(images)) { return []; }
      return images.flatMap(data => {
        const magic = typeof data === 'string' && IMAGE_MIME_BY_MAGIC.find(([prefix]) => data.startsWith(prefix));
        return magic ? [{ type: 'inline-image', url: `data:${magic[1]};base64,${data}` }] : [];
      });
    },
    _attachmentBlocks(attachment) {
      if (!attachment) { return []; }
      if (attachment.contentType.startsWith('image/')) {
        return [{ type: 'image', url: attachment.previewUrl, sending: true }];
      }
      if (attachment.contentType.startsWith('video/')) {
        return [{ type: 'video', url: attachment.previewUrl, sending: true }];
      }
      return [{ type: 'text', value: `📎 ${attachment.name}` }];
    },
    _clearAttachmentSending(messageId) {
      const message = this.messages.find(m => m._id === messageId);
      if (!message) { return; }
      message.payload.blocks
        .filter(isMediaBlock)
        .forEach(b => this.$set(b, 'sending', false));
    },
    // ─── Message rendering ─────────────────────────────────────────────
    appendMessage({ senderId, blocks, createdAt = null }) {
      this.messages = [
        ...this.messages,
        {
          _id: String(this.nextId),
          senderId,
          createdAt,
          payload: { blocks },
        },
      ];
      this.nextId += 1;
      this.$nextTick(this.scrollToBottom);
    },
    // The reply lands complete; its words blur in over the duration the
    // tokenizer computes, and the message counts as arriving until then.
    // Reduced motion shows the words at once (CSS), so nothing waits either.
    async appendAnimatedMessage(senderId, blocks, nonceAtStart) {
      const messageId = String(this.nextId);
      this.nextId += 1;
      this.messages = [...this.messages, { _id: messageId, senderId, payload: { blocks } }];
      this.revealingMessageId = messageId;
      this.$nextTick(this.scrollToBottom);
      try {
        if (nonceAtStart === this.chatSessionNonce && !prefersReducedMotion()) {
          await this.sleep(revealDurationMs(blocks, REVEAL_CONFIG));
        }
      } finally {
        if (this.revealingMessageId === messageId) { this.revealingMessageId = null; }
        // canRefreshNow() also gates on revealingMessageId — a chat event that
        // arrived while the reveal was still running only gets drained here, or
        // from sendUserText's own finally. Without this, it could sit deferred
        // forever if the customer never sends another message. Skipped once the
        // session has moved on: switching threads reset the pending flags, and
        // this must not fetch or mark-read a thread nobody is looking at.
        if (nonceAtStart === this.chatSessionNonce) { this.drainPendingRefresh(); }
      }
    },
    sleep(ms) {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    },
    bubbleClass(message) {
      const isUser = message.senderId === MESSAGE_ROLES.USER;
      return {
        'bubble-me': isUser,
        'bubble-me--media': isUser && this.isMediaOnly(message),
        'bubble-assistant': !isUser,
        'bubble-cx': message.senderId === MESSAGE_ROLES.CX,
      };
    },
    isMediaOnly(message) {
      const blocks = message.payload.blocks || [];
      return blocks.length > 0 && blocks.every(isMediaBlock);
    },
    // Skipped mid-send and mid-typing so it cannot wipe a message that has not
    // been persisted yet.
    refreshOnVisible() {
      if (document.hidden || !this.inlineCxEnabled) { return; }
      if (!this.canRefreshNow()) { return; }
      this.reloadCurrentConversation();
    },
    canRefreshNow() {
      // entering: a refresh re-keys every message, which would remount the rows
      // and replay the opening stagger from a blank thread.
      return Boolean(this.conversationId) && !this.pending && !this.revealingMessageId && !this.entering;
    },
    // Events for other conversations are ignored; the refetch on open covers them.
    handleChatEvent(event) {
      if (!event || !this.inlineCxEnabled) { return; }
      if (!event.conversationId || event.conversationId !== this.conversationId) { return; }
      // Deferred rather than dropped: a reply landing while the assistant is
      // still typing would otherwise stay invisible until the chat is reopened.
      if (!this.canRefreshNow()) {
        this.refreshPending = true;
        if (event.type === 'escalation-closed') { this.closePending = true; }
        return;
      }
      // Refetching covers both types: the response carries the messages and the
      // current escalation state, and (see reloadCurrentConversation) marks
      // the conversation read since it's the one the customer is looking at.
      const refreshed = this.reloadCurrentConversation();
      // The team usually closes the conversation while the customer is still
      // looking at it, so this — not the send path — is where most ratings are
      // asked for.
      if (event.type === 'escalation-closed') {
        refreshed.then(() => this.appendCsatCard());
      }
    },
    // Shared by reloadCurrentConversation and loadConversation: maps the raw
    // server response onto this.messages and applies the escalation state
    // that comes with it. Callers still each decide what to do about CSAT
    // and unread state, since those differ between a refresh and a fresh open.
    _applyFetchedMessages(data) {
      this.messages = _.chain(data.messages)
        .filter(m => Array.isArray(m.blocks) && m.blocks.every(isValidBlock))
        .map(m => ({
          _id: String(this.nextId++),
          senderId: this.senderIdForRole(m.role),
          authorName: m.authorName || null,
          createdAt: m.createdAt || null,
          payload: { blocks: [...m.blocks, ...this._storedImageBlocks(m.images)] },
        }))
        .value();
      this.escalation = data.escalation || null;
      if (this.escalation) {
        this.handedToTeam = true;
        // Found out here rather than escalating ourselves: the pending rating
        // belongs to the team CSAT now, so it must go with the hand-over.
        clearCandidate(this.conversationId);
      }
      this.ensureClosingNotice();
    },
    async reloadCurrentConversation() {
      const nonceAtStart = this.chatSessionNonce;
      try {
        const data = await CashflowAssistantApi.getConversationMessages(this.conversationId);
        if (nonceAtStart !== this.chatSessionNonce) { return; }
        // The server is the source of truth. Client-only blocks (notices, the
        // escalation card) are dropped by design — refreshes only run while idle,
        // so this cannot race a message that has not been sent yet.
        this._applyFetchedMessages(data);
        // Whatever brought this refresh about, the customer is looking at
        // this conversation right now — every reload path (not just the live
        // push) must clear the unread marker, or a message they already saw
        // via a focus/visibility refresh still reads as unread after a
        // full page reload, since that boot-time check has no other signal.
        this.markConversationRead(this.conversationId);
        const intercomConversationId = _.get(this.escalation, 'intercomConversationId');
        if (intercomConversationId && this.csatAskedFor === intercomConversationId
          && this.csatSubmittedFor !== intercomConversationId) {
          this.renderCsatCard(intercomConversationId);
        }
        this.$nextTick(this.scrollToBottom);
      } catch (error) {
        DDLogs.error('[inline-cx] Failed to refresh conversation', { error });
      }
    },
    senderIdForRole(role) {
      if (role === MESSAGE_ROLES.HUMAN) { return MESSAGE_ROLES.USER; }
      return role === MESSAGE_ROLES.CX ? MESSAGE_ROLES.CX : MESSAGE_ROLES.AI;
    },
    cxAuthorLabel(message) {
      const firstName = String(message.authorName || '').trim().split(' ')[0];
      return firstName ? `${firstName} ${CX_TEAM_LABEL}` : CX_TEAM_LABEL;
    },
    // Only messages the server has stored carry a time; an optimistic one gets
    // the local clock so the bubble does not change shape when it is confirmed.
    showTimestamp(message) {
      return Boolean(message.createdAt)
        && Boolean(this.escalation)
        && (message.senderId === MESSAGE_ROLES.CX || message.senderId === MESSAGE_ROLES.USER);
    },
    formatTimestamp(value) {
      return formatMessageTime(value);
    },
    // Only while opening a thread: a message that arrives live must not queue
    // behind a stagger, so the class comes off once the last row has landed.
    // Reduced motion skips the whole window — the CSS shows the rows at once
    // anyway, and canRefreshNow() gates on `entering`, so holding the flag
    // would only delay live updates for no visible animation.
    playEntrance() {
      clearTimeout(this.entranceTimer);
      if (prefersReducedMotion()) { return; }
      this.entering = true;
      this.entranceCount = this.messages.length;
      const steps = Math.min(this.entranceCount, ENTRANCE_STAGGERED_MESSAGES);
      this.entranceTimer = setTimeout(() => {
        this.entering = false;
        // A push that landed during the stagger was deferred, not dropped.
        this.drainPendingRefresh();
      }, steps * ENTRANCE_STEP_MS + ENTRANCE_DURATION_MS);
    },
    // Messages appended while the stagger is still running rise at once.
    entranceIndex(index) {
      if (index >= this.entranceCount) { return 0; }
      const firstStaggered = Math.max(0, this.entranceCount - ENTRANCE_STAGGERED_MESSAGES);
      return Math.max(0, index - firstStaggered);
    },
    bubbleRowClass(message) {
      const isUser = message.senderId === MESSAGE_ROLES.USER;
      return {
        'bubble-row-user': isUser,
        'bubble-row-assistant': !isUser,
        'bubble-row-cx': message.senderId === MESSAGE_ROLES.CX,
      };
    },
    // ─── Loader timing ─────────────────────────────────────────────────
    // Flip the given loading flag on only after LOADER_SHOW_DELAY_MS, so the
    // loader never flashes for fast loads. Always pair with clearLoaderDelay()
    // once loading finishes.
    scheduleLoader(setLoading) {
      this.clearLoaderDelay();
      this.loaderDelayTimer = setTimeout(() => {
        this.loaderDelayTimer = null;
        setLoading(true);
      }, LOADER_SHOW_DELAY_MS);
    },
    clearLoaderDelay() {
      if (this.loaderDelayTimer) {
        clearTimeout(this.loaderDelayTimer);
        this.loaderDelayTimer = null;
      }
    },
    // ─── Conversation history ──────────────────────────────────────────
    async fetchConversations() {
      this.scheduleLoader(v => { this.historyLoading = v; });
      try {
        const data = await CashflowAssistantApi.getConversations();
        this.conversations = data.conversations || [];
        clearCandidateIn(escalatedConversationIds(this.conversations));
      } catch (e) {
        DDLogs.error('Error fetching conversations:', e);
        this.conversations = [];
      } finally {
        this.clearLoaderDelay();
        this.historyLoading = false;
      }
    },
    async toggleHistoryView() {
      if (!this.showHistory) {
        this.showHistory = true;
        await this.fetchConversations();
        Segment.trackUserInteraction('AIChatbot_HistoryOpened');
      } else {
        this.showHistory = false;
      }
    },
    async loadConversation(conversation) {
      if (conversation.type === 'intercom') {
        Segment.trackUserInteraction('AIChatbot_IntercomConversationReopened', {
          intercomConversationId: conversation.intercomConversationId,
        });
        Intercom.showConversation(conversation.intercomConversationId);
        this.closeChat();
        return;
      }
      const selectedConversationId = conversation.conversation_id;
      this.switchConversation(selectedConversationId);
      this.showHistory = false;
      this.scheduleLoader(v => { this.conversationLoading = v; });
      const nonceAtStart = this.chatSessionNonce;
      try {
        const data = await CashflowAssistantApi.getConversationMessages(selectedConversationId);
        if (nonceAtStart !== this.chatSessionNonce) { this.clearLoaderDelay(); return; }
        // Opening a conversation is also how a client that missed a push catches up.
        this._applyFetchedMessages(data);
        // The team usually closes a conversation while the customer is away, so
        // opening it is the first chance to ask — the socket event and the send
        // path both need someone to already be here. Appended before
        // playEntrance so the card takes the last stagger slot instead of
        // rising before the rows above it.
        if (this.shouldAskForRating(this.escalation)) {
          await this.appendCsatCard();
          if (nonceAtStart !== this.chatSessionNonce) { return; }
        }
        this.markConversationRead(selectedConversationId);
        // If this conversation was escalated to a human, show the "transferred" card so the
        // user can reopen the Intercom chat. The merged conversations list carries an intercom
        // entry (cashflowAssistantConversationId → intercomConversationId) for it.
        const intercomEntry = _.find(this.conversations, {
          type: 'intercom',
          cashflowAssistantConversationId: selectedConversationId,
        });
        if (intercomEntry) {
          this.appendMessage({
            senderId: MESSAGE_ROLES.AI,
            blocks: [{
              type: 'escalation-transferred',
              status: 'pending',
              intercomConversationId: intercomEntry.intercomConversationId,
            }],
          });
        }
        this.playEntrance();
        Segment.trackUserInteraction('AIChatbot_ConversationLoaded', { conversationId: selectedConversationId });
      } catch (e) {
        DDLogs.error('Error loading conversation:', e);
      } finally {
        if (nonceAtStart === this.chatSessionNonce) {
          this.clearLoaderDelay();
          this.conversationLoading = false;
          // Scroll only after the loader hides and the chat-shell (v-if) renders —
          // its scroller ref doesn't exist while conversationLoading is true.
          this.$nextTick(this.scrollToBottom);
        }
      }
    },
    onConversationDeleted(deletedId) {
      this.conversations = this.conversations.filter(c => c.conversation_id !== deletedId);
      clearCandidate(deletedId);
      if (this.conversationId === deletedId) { this.switchConversation(null); }
    },
    openConversationMenu(conversation) {
      this.menuConversation = conversation;
      this.$nextTick(() => { this.showActionSheet = true; });
    },
    closeConversationMenu() {
      this.showActionSheet = false;
      this.menuConversation = null;
    },
    onImageOpen(block) {
      this.lightboxImageUrl = block.url;
    },
    closeLightbox() {
      this.lightboxImageUrl = null;
    },
    handleMenuContinue() {
      const conversation = this.menuConversation;
      this.closeConversationMenu();
      this.loadConversation(conversation);
    },
    handleMenuDelete() {
      this.showActionSheet = false;
      this.showDeleteConfirm = true;
    },
    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.menuConversation = null;
    },
    async handleDeleteConversation() {
      const id = this.menuConversation.conversation_id;
      this.deleteLoading = true;
      try {
        await CashflowAssistantApi.deleteConversation(id);
        Segment.trackUserInteraction('AIChatbot_ConversationDeleted', { conversationId: id });
        this.showDeleteConfirm = false;
        this.onConversationDeleted(id);
        this.menuConversation = null;
      } catch (e) {
        DDLogs.error('Error deleting conversation:', e);
        this.closeDeleteConfirm();
      } finally {
        this.deleteLoading = false;
      }
    },
    switchConversation(newConversationId) {
      this.conversationId = newConversationId;
      this.chatSessionNonce += 1;
      this.messages = [];
      this.pending = false;
      this.clearLoaderDelay();
      this.conversationLoading = false;
      this.lastClickedIntroQuestion = null;
      this.escalation = null;
      this.handedToTeam = false;
      this.refreshPending = false;
      this.closePending = false;
      // A reveal still sleeping for the old thread must not keep gating
      // canRefreshNow() — its message is gone with the wipe above.
      this.revealingMessageId = null;
      // The header reads this to decide whether a push deserves a badge.
      this.$store.commit('riseupChat/setOpenConversationId', newConversationId);
    },
    // Flag off: no candidate is ever written, so the feature leaves no trace.
    recordAiCsatCandidate({ escalated = false } = {}) {
      if (!this.aiChatCsatEnabled) {
        return;
      }
      recordCandidate({
        conversationId: this.conversationId,
        escalated: escalated || Boolean(this.escalation) || this.handedToTeam,
      });
    },
    // Client-only, like the escalation cards: the server never sends this block,
    // so the content security model is unchanged.
    async appendCsatCard() {
      // Keyed by intercomConversationId, not conversationId: a conversation
      // that gets escalated and closed a second time is a distinct rating
      // opportunity, even though it shares the same riseup-chat conversation.
      const intercomConversationId = _.get(this.escalation, 'intercomConversationId');
      if (!intercomConversationId) { return; }
      if (this.csatAskedFor === intercomConversationId) { return; }
      await this.ratedConversationsLoaded;
      if (this.alreadyRated(intercomConversationId)) { return; }
      this.csatAskedFor = intercomConversationId;
      this.renderCsatCard(intercomConversationId);
    },
    // The stored notice only exists for conversations closed since it was
    // introduced, and an append can fail. Rather than trust the thread to
    // contain it, the banner is derived from the escalation state whenever it
    // is missing — a closed conversation must never look like it just stopped.
    ensureClosingNotice() {
      if (_.get(this.escalation, 'status') !== 'closed') { return; }
      const hasClosingNotice = _.some(this.messages, m => _.some(
        _.get(m, 'payload.blocks'),
        b => b.type === 'notice' && b.variant === 'system' && b.noticeType === 'closed',
      ));
      if (hasClosingNotice) { return; }
      this.appendMessage({
        senderId: MESSAGE_ROLES.AI,
        blocks: [{ type: 'notice', variant: 'system', noticeType: 'closed', value: CX_ESCALATION_CLOSED_MESSAGE }],
      });
    },
    shouldAskForRating(escalation) {
      if (_.get(escalation, 'status') !== 'closed') { return false; }
      const closedAt = _.get(escalation, 'closedAt');
      if (!closedAt) { return false; }
      return Date.now() - new Date(closedAt).getTime() < CSAT_MAX_AGE_MS;
    },
    alreadyRated(conversationId) {
      const id = String(conversationId);
      return this.ratedConversations().includes(id)
        || this.ratedIntercomConversationIds.includes(id);
    },
    ratedConversations() {
      return readIdList(CSAT_RATED_STORAGE_KEY);
    },
    async fetchRatedConversations() {
      try {
        const { ratings } = await CashflowAssistantApi.getCsatRatings();
        this.ratedIntercomConversationIds = (ratings || []).map(r => String(r.intercomConversationId));
      } catch (error) {
        DDLogs.error('[inline-cx] Failed to load already-rated conversations', { error });
      }
    },
    rememberRated(conversationId) {
      rememberId(CSAT_RATED_STORAGE_KEY, conversationId, CSAT_RATED_MAX_ENTRIES);
    },
    renderCsatCard(intercomConversationId) {
      this.appendMessage({
        senderId: MESSAGE_ROLES.AI,
        blocks: [{
          type: 'csat', status: 'pending', score: 0, comment: '', title: this.csatTitle, intercomConversationId,
        }],
      });
    },
    onCsatScore({ block, score }) {
      this.$set(block, 'score', score);
    },
    onCsatComment({ block, comment }) {
      this.$set(block, 'comment', comment);
    },
    // The spacer stands in for the hidden composer so a thread scrolled to its
    // end does not drop when it grows.
    onCsatCommentFocus(field) {
      if (!hasCoarsePointer()) { return; }
      if (!this.csatCommentFocused) {
        this.composerSpacerHeight = this.$refs.composer ? this.$refs.composer.offsetHeight : 0;
      }
      this.csatCommentField = field;
      this.csatCommentFocused = true;
      this.startCsatReveal(field);
    },
    onCsatCommentBlur() {
      this.stopCsatReveal();
      this.csatCommentField = null;
      this.csatCommentFocused = false;
    },
    // The keyboard's resizes arrive with unknown timing; place the card once
    // they stop, and no earlier than the lift.
    startCsatReveal(field) {
      this.stopCsatReveal();
      const { scroller } = this.$refs;
      const liftDoneAt = Date.now() + COMPOSER_LIFT_MS;
      const schedule = () => {
        clearTimeout(this.csatRevealTimer);
        const wait = Math.max(CSAT_REVEAL_SETTLE_MS, liftDoneAt - Date.now());
        this.csatRevealTimer = setTimeout(() => {
          this.stopCsatReveal();
          this.revealCsatCard(field);
        }, wait);
      };
      if (scroller && typeof ResizeObserver === 'function') {
        this.csatRevealObserver = new ResizeObserver(schedule);
        this.csatRevealObserver.observe(scroller);
      }
      schedule();
    },
    stopCsatReveal() {
      clearTimeout(this.csatRevealTimer);
      if (this.csatRevealObserver) {
        this.csatRevealObserver.disconnect();
        this.csatRevealObserver = null;
      }
    },
    revealCsatCard(field) {
      const { scroller } = this.$refs;
      const card = field.closest('.csat-card');
      if (!scroller || !card) { return; }
      const view = scroller.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const fieldRect = field.getBoundingClientRect();
      const toCardBottom = cardRect.bottom - view.bottom;
      const toFieldTop = fieldRect.top - view.top - CSAT_REVEAL_MARGIN_PX;
      const delta = toFieldTop < 0 ? toFieldTop : Math.max(0, Math.min(toCardBottom, toFieldTop));
      if (!delta) { return; }
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
      } else {
        scroller.scrollTop += delta;
      }
    },
    async onCsatSubmit(block) {
      if (!block.score || block.status === 'loading') { return; }
      this.blurKeyboard();
      this.$set(block, 'status', 'loading');
      Segment.trackUserInteraction('CSATSurveyAnsweredClicked', {
        surveyAnswers: { CSATScore: block.score, CSATExperience: block.comment || null },
        conversationId: this.conversationId,
        source: 'riseup_chat',
      });
      try {
        await CashflowAssistantApi.submitCsat(this.conversationId, block.score, block.comment);
      } catch (error) {
        // The rating is already recorded in Segment, so the customer is thanked
        // either way rather than being asked to rate a second time.
        DDLogs.error('[inline-cx] Failed to submit CSAT to the team', { error });
      }
      this.csatSubmittedFor = block.intercomConversationId;
      this.rememberRated(block.intercomConversationId);
      this.$set(block, 'status', 'submitted');
    },
    markConversationRead(conversationId) {
      if (!this.inlineCxEnabled) { return; }
      this.$store.dispatch('riseupChat/markRead', conversationId);
    },
    startNewConversation() {
      this.switchConversation(null);
      this.showHistory = false;
      Segment.trackUserInteraction('AIChatbot_NewConversation');
    },

    persistRecentConversation() {
      try {
        if (this.conversationId) {
          sessionStorage.setItem(
            CHAT_CONVERSATION_RESUME_KEY,
            JSON.stringify({ conversationId: this.conversationId, timestamp: Date.now() }),
          );
        } else {
          sessionStorage.removeItem(CHAT_CONVERSATION_RESUME_KEY);
        }
      } catch (error) {
        DDLogs.error('Error persisting recent conversation:', error);
      }
    },
    // Decide which conversation to open when the chatbot mounts. An escalated
    // conversation (one with an associated Intercom/team conversation) takes
    // precedence: we reopen the RiseUp-chat conversation that led to it so the
    // user lands on the thread carrying the "transferred to team" button.
    // Falls back to the sessionStorage time-window resume when none exist.
    async restoreInitialConversation() {
      // An explicitly requested thread wins over both the escalation rule and
      // the resume record.
      if (this.initialConversationId) {
        try {
          await this.fetchConversations();
        } catch (e) {
          DDLogs.error('Error fetching conversations for a deep-opened thread:', e);
        }
        if (this.destroyed) { return; }
        this.loadConversation({ conversation_id: this.initialConversationId });
        return;
      }
      const escalatedId = await this.findLatestEscalatedConversationId();
      if (this.destroyed) { return; }
      if (escalatedId) {
        // fetchConversations (called above) has populated this.conversations, so
        // loadConversation can append the escalation-transferred card + button.
        this.loadConversation({ conversation_id: escalatedId });
        return;
      }
      const recordToRestore = this.readRestorableConversationRecord();
      if (recordToRestore) {
        this.loadConversation({ conversation_id: recordToRestore.conversationId });
      }
    },
    // Returns the conversation_id of the RiseUp-chat conversation to auto-reopen on
    // mount because it was escalated to the team, or null if there's none.
    // Populates this.conversations as a side effect so loadConversation can later
    // match the intercom entry.
    //
    // We only reopen the escalation if it's still the user's most recent chat. If
    // the user started a new conversation after the escalation, that newer chat is
    // the latest activity, so we ignore the "reopen escalation" rule and fall back
    // to the normal resume behavior.
    async findLatestEscalatedConversationId() {
      try {
        await this.fetchConversations();
      } catch (e) {
        DDLogs.error('Error fetching conversations for escalated restore:', e);
        return null;
      }
      // The backend returns conversations newest-first, so the first non-intercom
      // entry is the user's most recent conversation.
      const mostRecentChat = _.find(this.conversations, c => c.type !== 'intercom');
      if (!mostRecentChat) { return null; }
      if (_.get(mostRecentChat, 'escalation.status') === 'open') {
        return mostRecentChat.conversation_id;
      }
      const escalatedParentIds = _.chain(this.conversations)
        .filter(c => c.type === 'intercom' && c.cashflowAssistantConversationId)
        .map('cashflowAssistantConversationId')
        .uniq()
        .value();
      return escalatedParentIds.includes(mostRecentChat.conversation_id) ? mostRecentChat.conversation_id : null;
    },
    readRestorableConversationRecord() {
      let stored;
      try {
        const raw = sessionStorage.getItem(CHAT_CONVERSATION_RESUME_KEY);
        if (!raw) { return null; }
        stored = JSON.parse(raw);
      } catch (error) {
        sessionStorage.removeItem(CHAT_CONVERSATION_RESUME_KEY);
        return null;
      }
      const { conversationId, timestamp } = stored || {};
      if (!conversationId || !timestamp
        || Date.now() - timestamp > CHAT_CONVERSATION_RESUME_WINDOW_MS) {
        sessionStorage.removeItem(CHAT_CONVERSATION_RESUME_KEY);
        return null;
      }
      return { conversationId, timestamp };
    },

    // ─── Escalation ────────────────────────────────────────────────────
    applyEscalation(intercomConversationId, extraTrackingProps = {}) {
      // The team owns this thread now; the team CSAT covers it.
      this.handedToTeam = true;
      clearCandidate(this.conversationId);
      Segment.trackUserInteraction('cashflow_assistant_escalated_to_intercom', {
        conversationId: this.conversationId,
        intercomConversationId,
        inline: this.inlineCxEnabled,
        ...extraTrackingProps,
      });
      // Inline, the team joins this conversation, so the chat stays open;
      // otherwise fall back to the Intercom messenger.
      if (this.inlineCxEnabled) {
        this.escalation = { status: 'open', intercomConversationId };
        return;
      }
      Intercom.showConversation(intercomConversationId);
      this.closeChat();
    },
    async startTeamConversation() {
      const nonceAtStart = this.chatSessionNonce;
      // Immediate, not scheduleLoader: the 400ms grace period would leave the
      // intro suggestions clickable, starting a second conversation this
      // response would then clobber. A team start is never that fast anyway.
      this.conversationLoading = true;
      try {
        const data = await CashflowAssistantApi.startTeamConversation(this.initiator);
        if (this.destroyed || nonceAtStart !== this.chatSessionNonce) { return; }
        if (data.openConversationElsewhere) {
          Segment.trackUserInteraction('cashflow_assistant_team_start_joined_open_conversation', {
            conversationId: data.openConversationElsewhere, initiator: this.initiator,
          });
          await this.fetchConversations();
          if (this.destroyed || nonceAtStart !== this.chatSessionNonce) { return; }
          await this.loadConversation({ conversation_id: data.openConversationElsewhere });
          return;
        }
        // A 2xx without ids (e.g. a fail-open rate limiter) must not render a
        // handover no team ever received.
        if (!data.conversationId || !data.intercomConversationId) {
          throw new Error('team_start_bad_response');
        }
        this.conversationId = data.conversationId;
        // Local copy of the seeded notice; a refresh replaces it with the server's.
        this.appendMessage({
          senderId: MESSAGE_ROLES.AI,
          blocks: [{ type: 'notice', variant: 'system', noticeType: 'open', value: CX_ESCALATION_STARTED_MESSAGE }],
        });
        this.applyEscalation(data.intercomConversationId, { teamStart: true, initiator: this.initiator });
      } catch (error) {
        if (this.destroyed || nonceAtStart !== this.chatSessionNonce) { return; }
        DDLogs.error('[escalation] Failed to start a conversation with the team', {
          initiator: this.initiator, status: _.get(error, 'response.status'),
        });
        this.closeChat();
        if (this.startWithTeamFallback) {
          this.startWithTeamFallback();
        } else {
          Intercom.show();
        }
      } finally {
        if (!this.destroyed) {
          this.clearLoaderDelay();
          this.conversationLoading = false;
        }
      }
    },
    async escalateConversationSafely(conversationId) {
      try {
        return await CashflowAssistantApi.escalateConversation(conversationId);
      } catch (error) {
        DDLogs.error('[escalation] Failed to escalate conversation', { error });
        return null;
      }
    },
    // Open the human-rep chat for an escalation card. The Intercom id is reused when already
    // known (history), otherwise we escalate the current conversation (live; the backend
    // reuses any open chat). Drives the card's status so the button shows loading/error.
    async openEscalation(block, segmentEvent) {
      if (block.status === 'loading') { return; }
      this.$set(block, 'status', 'loading');
      Segment.trackUserInteraction(segmentEvent, {
        conversationId: this.conversationId,
        intercomConversationId: block.intercomConversationId,
      });
      const knownId = block.intercomConversationId;
      if (!knownId && !this.conversationId) {
        DDLogs.error('[escalation] Escalation clicked without conversationId');
        this.$set(block, 'status', 'error');
        return;
      }
      const escalation = knownId ? null : await this.escalateConversationSafely(this.conversationId);
      const intercomConversationId = knownId || _.get(escalation, 'intercomConversationId');
      if (!intercomConversationId) {
        this.$set(block, 'status', 'error');
        return;
      }
      await this.sleep(ESCALATION_DISPLAY_DURATION_MS);
      this.applyEscalation(intercomConversationId);
    },
    onEscalationAccept(block) {
      return this.openEscalation(block, 'cashflow_assistant_escalation_consent_clicked');
    },
    onEscalationOpen(block) {
      return this.openEscalation(block, 'cashflow_assistant_open_existing_conversation_clicked');
    },
    onEscalationOpenElsewhere(block) {
      Segment.trackUserInteraction('cashflow_assistant_open_conversation_elsewhere_clicked', {
        conversationId: block.conversationId,
      });
      this.loadConversation({ conversation_id: block.conversationId });
    },
    onEscalationDismiss(block) {
      if (block.status === 'loading' || block.status === 'dismissed') { return; }
      this.$set(block, 'status', 'dismissed');
      Segment.trackUserInteraction('cashflow_assistant_escalation_consent_dismissed');
    },

    // ─── Layout / viewport ─────────────────────────────────────────────
    closeChat() {
      const { sheet } = this.$refs;
      if (sheet && typeof sheet.closeSheet === 'function') {
        sheet.closeSheet();
        return;
      }
      this.$emit('close');
    },
    // An image or video finishing its load grows the scroller after the pin
    // that ran when its message landed; re-pin unless the customer has since
    // scrolled up to read history.
    onMediaLoaded() {
      const el = this.$refs.scroller;
      if (!el) { return; }
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= MEDIA_REPIN_THRESHOLD_PX) { this.scrollToBottom(); }
    },
    scrollToBottom({ smooth = false } = {}) {
      const el = this.$refs.scroller;
      if (!el) { return; }
      if (smooth && typeof el.scrollTo === 'function') {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    },
    blurKeyboard() {
      const { activeElement } = document;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        activeElement.blur();
      }
      const { input } = this.$refs;
      if (input) { input.blur(); }
      const container = this.$el;
      if (container) {
        container.setAttribute('tabindex', '-1');
        container.focus();
        setTimeout(() => container.removeAttribute('tabindex'), 100);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.chatbot-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 16px;
  padding-top: 4px;
  color: #000000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;

  .header-action-icon-wrapper {
    position: relative;
    display: inline-flex;
  }

  .header-action-icon {
    cursor: pointer;
    z-index: 1;
    width: 24px;
    height: 24px;
    vertical-align: middle;
  }

  .header-action-icon-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #F34F8A;
    pointer-events: none;
  }

  .close {
    cursor: pointer;
    z-index: 1;
  }
}

.row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon {
  width: 20px;
  height: 20px;
}

/* Once a conversation has started the pink logo is replaced in place by the
   new-chat icon, which starts a fresh conversation when tapped. */
.icon-new-chat {
  width: 24px;
  height: 24px;
  cursor: pointer;
  z-index: 1;
}

.title {
  font-size: 24px;
  line-height: 24px;
  font-weight: 600;
}

/* Positioning context so the leaving (logo+title) and entering (new-chat) states
   overlap during the swap instead of reflowing the header. Both rows are absolutely
   anchored to the same spot (start of the header, vertically centred) so neither
   re-positions when the transition begins — the earlier version flipped the leaving
   row from static flow to absolute mid-transition, which snapped it sideways before
   the animation even started. min-width keeps the box from collapsing while only the
   (narrow) new-chat icon is present. */
.header-brand {
  position: relative;
  min-height: 24px;
  min-width: 130px;

  .row {
    position: absolute;
    top: 50%;
    right: 0;
    /* Vertical centring lives in `transform`; the animated drift uses the separate
       `translate` property so the two compose without fighting each other. */
    transform: translateY(-50%);
    white-space: nowrap;
  }
}

/* Logo+title leaves: fade out while drifting up (0.2s). New-chat icon enters: after
   the leave finishes (0.2s delay) it fades in while drifting up into place. */
.brand-swap-leave-active {
  transition: opacity 0.2s ease, translate 0.2s ease;
}

.brand-swap-enter-active {
  transition: opacity 0.2s ease, translate 0.2s ease;
  transition-delay: 0.2s;
}

.brand-swap-leave-to {
  opacity: 0;
  translate: 0 -8px;
}

.brand-swap-enter-from,
.brand-swap-enter {
  opacity: 0;
  translate: 0 8px;
}

@media (prefers-reduced-motion: reduce) {
  .brand-swap-leave-active,
  .brand-swap-enter-active {
    transition: none;
  }
}

.chat-shell {
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #FCF7ED 0%, #F8F1DE 100%);
  font-family: SimplerPro, sans-serif;
  /* Fill the height-controlling .chat-body wrapper rather than imposing our own
     height — a second fixed height here would overflow the wrapper and surface a
     scrollbar on the sheet. */
  height: 100%;
  /* BaseUI BottomSheet has align-items: center on its flex children, so
     children that don't declare a width get sized to content and centered,
     leaving the sheet's white background visible on both sides. */
  width: 100%;
}

/* Beige backdrop behind the three body views. It owns the chat's height and the
   background, so: (1) the white sheet never shows through while a view fades in,
   (2) the height stays steady (no scrollbar / resize flash) as views swap, and
   (3) the panel can animate smoothly when its height genuinely changes (keyboard,
   viewport resize). The views inside fill it via position:absolute. */
.chat-body {
  position: relative;
  width: 100%;
  overflow: hidden;
  /* Fill the remaining sheet height below the (handle + header) container. The
     sheet is a flex column sized to the viewport, so flex-fill gives the chat its
     full height without a fragile JS measurement of the header — which used to race
     header layout and clip the top of the body when it measured 0. min-height: 0
     lets this flex item shrink below its content instead of overflowing the sheet. */
  flex: 1;
  min-height: 0;
  background: linear-gradient(180deg, #FCF7ED 0%, #F8F1DE 100%);
}

/* Each view fills the .chat-body box and stacks in the same place, so a fading-in
   view overlaps (not pushes) any leaving content — no layout shift, no scrollbar. */
.chat-body > .pre-loading,
.chat-body > .conversation-history,
.chat-body > .chat-shell {
  position: absolute;
  inset: 0;
  height: 100%;
}

/* Subtle fade between the chat's three body states (loader / history / chat),
   and on conversation switch (chat-shell is keyed on chatSessionNonce). Enter-only:
   the leaving view is removed instantly and the new one fades in over the beige
   backdrop — fading the leave too (mode=out-in) leaves a gap where the sheet body
   collapses to white. */
.chat-view-enter-active {
  transition: opacity 0.28s ease;
}

.chat-view-enter {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .chat-view-enter-active {
    transition: opacity 0.1s ease;
  }
}

.pre-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: linear-gradient(180deg, #FCF7ED 0%, #F8F1DE 100%);
  font-family: SimplerPro, sans-serif;

  lottie-player {
    width: 120px;
    height: 120px;
  }
}

.pre-loading-text {
  display: block;
  text-align: center;
  font-size: 18px;
  color: #202125;
}

.messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* The system notice bleeds past the screen edges (see MessageBlocks); clip
     rather than grow a horizontal scrollbar. */
  overflow-x: hidden;
  --messages-side-padding: 20px;
  padding: 0 var(--messages-side-padding) 32px;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
  gap: 4px;
  /* Force LTR so flex-end is the right side regardless of page direction;
     bubbles override back to RTL for their Hebrew content. */
  direction: ltr;
}

.intro-suggestions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  align-self: flex-end;
  margin-top: auto;
  margin-bottom: 8px;
  direction: rtl;
}

.intro-button {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-family: inherit;
  font-weight: 500;
  padding: 12px 18px 14px;
  border-radius: 20px;
  background-color: #FFFFFF;
  color: #202125;
  border: none;
  cursor: pointer;
  box-shadow: 0 18px 32.4px rgba(241, 233, 217, 1);

  &:hover {
    background-color: #F8F8F8;
  }

  .intro-arrow {
    margin-right: 8px;
    width: 12px;
  }
}

/* Bubble styles mirror V1's USER_BUBBLE_STYLE / AI_BUBBLE_STYLE. */
.bubble {
  direction: rtl;
  text-align: right;
  word-wrap: break-word;
  color: #202125;
}

.bubble-row {
  width: 100%;
}

.messages--entering .bubble-row {
  animation: enterMessage var(--enter-duration) cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(var(--enter-index, 0) * var(--enter-step));
}

@keyframes enterMessage {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .messages--entering .bubble-row {
    animation: none;
  }
}

.bubble-row-user {
  text-align: right;
}

/* The sheet centres text, which would centre the inline team card too. */
.bubble-row-cx {
  text-align: left;
}

.bubble-me {
  display: inline-flex;
  flex-direction: column;
  gap: 20px;
  background-color: #ffffff;
  border-radius: 20px;
  max-width: 80%;
  padding: 20px;
  margin: 6px 0;
  box-shadow: 0 18px 16.2px rgba(241, 233, 217, 1);
  opacity: 0;
  animation: slideInUserMessage 0.25s ease-out forwards;
}

/* Figma: Flows for CX, node 1369:1267. */
.bubble-me--media {
  gap: 11px;
  background-color: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

@keyframes slideInUserMessage {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUserMessage {
  from { opacity: 0; }
  to { opacity: 1; }
}

.bubble-assistant {
  background-color: transparent;
  border-radius: 23px;
  padding: 10px 0;
  margin: 10px 0;
  color: #000000;
}

.composer-file-input {
  display: none;
}

/* Figma: intercom in RiseUp Chat, node 17092:76135. The box is the send
   button's 40px rather than the icon's 24px so the two align while the pill
   grows with a multi-line draft; the negative margin then puts the icon at
   Figma's 18px from the edge despite padding meant for the text field. */
.composer-attach {
  width: 40px;
  height: 40px;
  margin-right: -10px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.attachment-chip-list {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 12px 16px 8px;
}

/* Figma: intercom in RiseUp Chat, node 17155:13019. */
.attachment-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  padding: 7px 15px 7px 9px;
  border: 1px solid rgba(32, 33, 37, 0.08);
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 4px 6.7px rgba(0, 0, 0, 0.04);
  font-size: 12px;
  max-width: 70%;
}

.attachment-chip__thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background-color: rgba(32, 33, 37, 0.05);
}

.attachment-chip__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #202125;
}

.attachment-chip__divider {
  width: 1px;
  align-self: stretch;
  flex-shrink: 0;
  background-color: rgba(32, 33, 37, 0.08);
}

.attachment-chip__remove {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.attachment-chip__spinner {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid rgba(32, 33, 37, 0.15);
  border-top-color: #202125;
  animation: attachment-chip-spin 0.8s linear infinite;
}

@keyframes attachment-chip-spin {
  to { transform: rotate(360deg); }
}

/* Figma: Flows for CX, node 1365:1028. A blue card on the opposite side from the
   customer's bubbles, so a teammate's reply does not read as the assistant's. */
.bubble-cx {
  display: inline-flex;
  flex-direction: column;
  gap: 20px;
  max-width: calc(100% - 55px);
  background-color: #5d7afd;
  border: 1px solid rgba(0, 0, 0, 0.03);
  border-radius: 20px;
  padding: 20px;
  margin: 6px 0 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 18px 32.4px rgba(229, 216, 187, 0.48);
}

.bubble-cx :deep(.block-text) {
  color: #ffffff;
  /* block-text's subpixel smoothing renders the message heavier than the
     label and timestamp sharing the card. */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.bubble-cx :deep(.block-link) {
  color: #ffffff;
}

.cx-author-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cx-author-badge {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.cx-author,
.bubble-time {
  font-size: 12px;
  line-height: 16px;
  color: #202125;
  opacity: 0.3;
}

.bubble-cx .cx-author,
.bubble-cx .bubble-time {
  color: #ffffff;
  opacity: 0.7;
}

/* Figma-free: chosen in the text-motion lab. Words are laid out in place and
   sharpen in one after another; the delay per word comes from the tokenizer. */
.bubble-assistant.is-revealing :deep(.word) {
  opacity: 0;
  filter: blur(6px);
  animation: revealWord var(--reveal-duration) ease-out forwards;
  animation-delay: var(--reveal-delay, 0ms);
}

@keyframes revealWord {
  to {
    opacity: 1;
    filter: blur(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bubble-assistant.is-revealing :deep(.word) {
    animation: none;
    opacity: 1;
    filter: none;
  }
}

.loading {
  padding: 10px 0;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding-top: 8px;
  opacity: 0;
  animation: fadeInUserMessage 0.2s ease-in forwards;
}

/* Matches V1's "אני על זה..." shimmer text from Chatbot.vue's AUXILIARY_STYLE. */
.text-shimmer {
  display: inline-flex;
  align-items: center;
  height: 26px;
  font-size: 18px;
  line-height: 1;
  color: #202125;
  opacity: 0.7;
  background: linear-gradient(
    90deg,
    #202125 0%,
    #202125 40%,
    rgba(32, 33, 37, 0.5) 50%,
    #202125 60%,
    #202125 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.composer-spacer {
  flex-shrink: 0;
}

.composer {
  display: flex;
  flex-direction: column;
  padding: 12px 20px 20px;
  background: transparent;
}

/* Figma: intercom in RiseUp Chat, node 17155:11906. Absolutely positioned so
   it never affects the pill's own size/position - it sits behind the chip
   and the top of the pill, hidden below wherever the opaque pill covers it. */
.composer-input-area {
  position: relative;
  display: flex;
  flex-direction: column;
}

.composer-input-area__bg {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(32, 33, 37, 0.08);
  border-radius: 20px;
  background-color: #fffcf5;
}

/* Disclaimer moved out of the header to sit centered beneath the input field. */
.composer-disclaimer {
  box-sizing: border-box;
  padding-top: 12px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #202125;
}

.disclaimer-enter-active,
.disclaimer-leave-active {
  overflow: hidden;
  transition: height 0.28s ease, padding-top 0.28s ease, opacity 0.2s ease;
}

.disclaimer-enter,
.disclaimer-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .disclaimer-enter-active,
  .disclaimer-leave-active {
    transition: none;
  }
}

.composer-pill {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  min-height: 58px;
  border-radius: 29px;
  background-color: #ffffff;
  box-shadow: 0 18px 32.4px rgba(241, 233, 217, 1);
  padding: 9px 20px 9px 6px;
  direction: ltr;
}

.composer-input {
  flex: 1;
  border: none;
  padding: 8px 16px;
  font-size: 16px;
  line-height: 24px;
  font-family: inherit;
  font-weight: 500;
  background: transparent;
  direction: rtl;
  text-align: right;
  outline: none;
  caret-color: #5d7afd;
  resize: none;
  max-height: 144px;
  overflow-y: auto;

  &::placeholder {
    color: #999;
    font-weight: 400;
  }

  &:disabled {
    opacity: 0.6;
  }
}

.composer-send {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
}

/* ─── Action sheet + delete confirm overlays (ported from V1) ─── */

.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9999;
  animation: overlayFadeIn 0.3s ease forwards;
  background: rgba(0, 0, 0, 0);
}

@keyframes overlayFadeIn {
  from { background: rgba(0, 0, 0, 0); }
  to { background: rgba(0, 0, 0, 0.4); }
}

.fullscreen-overlay--center {
  align-items: center;
  padding: 24px;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  object-fit: contain;
}

.lightbox-close {
  position: absolute;
  top: 24px;
  inset-inline-end: 24px;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.action-sheet {
  background: #FFFFFF;
  border-radius: 20px 20px 0 0;
  width: 100%;
  padding: 0 16px 32px;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.action-sheet-handle {
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  margin: 12px auto 16px;
}

.action-sheet-menu {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-sheet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: #202125;
  direction: rtl;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.action-icon {
  width: 24px;
  height: 24px;
}

.confirm-card {
  background: #FFFFFF;
  border-radius: 24px;
  max-width: 360px;
  width: 100%;
  overflow: hidden;
}

.confirm-card :deep(.top) { padding-bottom: 16px; }
.confirm-card :deep(.buttons-wrapper) { margin-top: 8px; }
.confirm-card :deep(.riseup-button) {
  border-radius: 16px;
  height: 61px;
}
.confirm-card :deep(.title) { line-height: 32px; }

.delete-subtitle {
  font-family: SimplerPro, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: right;
  color: #000000;
}

.cx-update-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  background: #FFFFFF;
  border-radius: 20px;
  padding: 24px;
  margin: 0 0 16px;
  box-sizing: border-box;
  flex-shrink: 0;

  &__illustration {
    position: relative;
    flex: 0 0 88px;
    height: 100px;
    align-self: center;
    margin-top: -10px;
  }

  &__flower {
    position: absolute;
    top: 12px;
    left: 0;
    width: 76px;
    height: 76px;
  }

  &__person {
    position: absolute;
    top: 12px;
    left: 0;
    width: 87px;
    height: 87px;
    object-fit: contain;
  }

  &__new-label {
    position: absolute;
    top: -8px;
    left: -4px;
    transform: rotate(-27deg);
    transform-origin: left top;
    direction: rtl;
    font-family: 'Sunny', SimplerPro_Alte-Bold, sans-serif;
    font-style: italic;
    font-size: 28px;
    line-height: 1;
    color: #F34F8A;
    pointer-events: none;
    z-index: 1;
    white-space: nowrap;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: right;
    direction: rtl;
    gap: 10px;
  }

  &__title {
    font-family: SimplerProAlte, SimplerPro, sans-serif;
    font-weight: 700;
    font-size: 22px;
    line-height: 30px;
    color: #08134C;
  }

  &__body {
    font-family: SimplerPro, sans-serif;
    font-size: 15px;
    line-height: 22px;
    color: #000000;
  }

  &__cta {
    margin-top: 4px;
    font-family: SimplerPro, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #5D7AFD;
    text-decoration: underline;
    text-underline-offset: 4px;
    cursor: pointer;
  }
}

/* Make the chat sheet a full-height page (covers the whole overlay) instead of the
   default content-sized bottom sheet. Fill the overlay (which is already inset below
   the app chrome / safe area and flex-sized to the available space) rather than the
   raw window height — --max-height is the full viewport, which is taller than the
   overlay and, since the overlay bottom-aligns its child, pushed the sheet's top
   (handle + header) off the top of the screen. height: 100% fills the overlay
   exactly, giving the inner flex column a definite height so .chat-body flex-fills
   the remainder below the header. */
/* Square top corners — at full height the sheet reaches the top of the overlay,
   so the rounded corners are no longer wanted. The header container inherits its
   radius from the sheet via a CSS var, so flatten it too. */
:deep(.bottom-sheet) {
  border-radius: 0;
  height: 100%;
}
:deep(.bottom-sheet .sheet-header-container) { border-radius: 0; }
.chatbot-overlay-open :deep(.bottom-sheet) { overflow: hidden; }
/* The sheet always fills the overlay, so its beige paints under the keyboard
   and through the band iOS misreports above it; the content lifts by the
   keyboard inset PageOverlay (trackVisualViewport) publishes, gliding with the
   keyboard rather than waiting for the base-ui remeasure. */
.chatbot-bottom-sheet :deep(> .bottom-sheet) {
  box-sizing: border-box;
  height: 100%;
  max-height: 100%;
  padding-bottom: var(--keyboard-inset, 0px);
  transition: padding-bottom 0.25s ease;
}

@media (prefers-reduced-motion: reduce) {
  .chatbot-bottom-sheet :deep(> .bottom-sheet) {
    transition: none;
  }
}

.chatbot-history-mode :deep(> .overlay > .bottom-sheet) {
  background: linear-gradient(180deg, #FCF7ED 0%, #F8F1DE 100%);
}
/* In history mode the list rows carry their own spacing and dividers, so the
   sheet header's default 24px bottom padding left the first row floating below
   the header. Remove it so the list sits directly under the header. */
.chatbot-history-mode :deep(.sheet-header-container .padding-bottom-ml) {
  padding-bottom: 0;
}
</style>
